import { Box, Flex, Text, Heading, Grid } from '@radix-ui/themes';
import { Image } from '@trashlab/sitesapp/src/organizations/best-deal/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/best-deal/contexts/use-tenant-context';
import { IconCheckmark } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/icons/icons';
import { GoogleReviews } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/shared/google-reviews';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/get-variant-value';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/use-theme-text-color';
import AnimatedTextBlock from '@trashlab/sitesapp/src/organizations/best-deal/components/shared/animated-text-block';
import { ServiceOptions } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/home/service-options';
import { Button } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/shared/button';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

export const HomeHeroSection = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);
  const { textColorClass, isLightBackground } = useThemeTextColor();

  const homeHeroImage = useVariantValue(
    'HOME_HERO_IMAGE',
    '/default/hero-section-image.svg',
  );
  const homeHeroBackgroundImage = useVariantValue(
    'HOME_HERO_BACKGROUND_IMAGE',
    '/default/hero-section-background.svg',
  );

  const homeHeroTitle = useVariantValue(
    'HOME_HERO_TITLE',
    `${website?.organization?.name || 'our company'} rental ${
      orgAddress?.city ?? 'San Francisco'
    }`,
  );

  const homeHeroFeatures = useVariantValue('HOME_HERO_FEATURE', [
    { value: '100% satisfaction guarantee', orderIndex: 0 },
    { value: 'Residential & commercial dumpster rental', orderIndex: 1 },
    {
      value: `Voted ${
        orgAddress?.state ?? 'CA'
      } best same day dumpster rental`,
      orderIndex: 2,
    },
  ]);

  // Check if tenant wants Contact Us CTA instead of service options
  const showContactCta = useVariantValue('HOME_HERO_SHOW_CONTACT_CTA', '');
  const contactCtaText = useVariantValue(
    'HOME_HERO_CONTACT_CTA_TEXT',
    'Contact Us',
  );
  const shouldShowContactCta = showContactCta.toLowerCase() === 'true';

  return (
    <Grid
      columns={{ initial: '1', xs: '2' }}
      width="auto"
      justify="center"
      align="center"
      className="relative min-h-[360px] overflow-hidden"
    >
      <Box
        className="absolute inset-0 -z-20 no-animate"
        data-variable="HOME_HERO_BACKGROUND_IMAGE"
      >
        <Image
          src={homeHeroBackgroundImage}
          alt="Background"
          className="w-full h-full object-cover"
        />
      </Box>
      <Box className="absolute inset-0 -z-10 bg-[var(--background-color)] opacity-90 no-animate" />

      <Box
        className="order-2 xs:order-1 relative z-10 flex-1 h-full min-h-[324px] xs:min-h-[450px] sm:min-h-[720px] p-0 no-animate"
        data-variable="HOME_HERO_IMAGE"
      >
        <Box className="absolute inset-0 overflow-hidden no-animate">
          <Image
            src={homeHeroImage}
            alt="Dumpster"
            className="w-full h-full object-cover object-center"
          />
        </Box>
      </Box>

      <Flex
        direction="column"
        p="8"
        gap="3"
        className="order-1 xs:order-2 relative z-10"
      >
        <AnimatedTextBlock delay={250}>
          <Flex direction="column" gap="6">
            <Heading
              as="h1"
              size={{ initial: '8', md: '9' }}
              className={textColorClass}
              data-variable="HOME_HERO_TITLE"
            >
              {homeHeroTitle}
            </Heading>

            <Flex direction="column" gap="3">
              {Array.isArray(homeHeroFeatures) &&
                [...homeHeroFeatures]
                  .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
                  .map((feature) => (
                    <Flex
                      key={`${feature.orderIndex}-${feature.value}`}
                      align="center"
                      gap="2"
                      data-variable="HOME_HERO_FEATURE"
                      data-order-index={feature.orderIndex}
                    >
                      <IconCheckmark
                        color={isLightBackground ? 'black' : 'white'}
                        className="w-6 h-6"
                      />
                      <Text
                        size={{ initial: '3', md: '5' }}
                        weight="bold"
                        className={textColorClass}
                      >
                        {feature.value}
                      </Text>
                    </Flex>
                  ))}
            </Flex>

            {shouldShowContactCta ? (
              <Box className="w-full">
                <Button
                  href="/contact-us"
                  size="large"
                  variant="primary"
                  title="Contact us to learn more about our services"
                  aria-label="Navigate to the contact form to get in touch with us"
                  data-variable="HOME_HERO_CONTACT_CTA_TEXT"
                >
                  {contactCtaText}
                </Button>
              </Box>
            ) : (
              <ServiceOptions />
            )}

            <GoogleReviews dark={!isLightBackground} />
          </Flex>
        </AnimatedTextBlock>
      </Flex>
    </Grid>
  );
};

export default HomeHeroSection;
